/**
 * Refund Policy Page
 */

import { Link } from 'react-router-dom';
import { RefreshCw, Download, Lock, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

export default function RefundPolicyPage(): JSX.Element {
  return (
    <div className="container-custom py-12">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <RefreshCw size={32} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100">
                Refund Policy
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Last updated: {new Date().toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        {/* Important Notice */}
        <div className="card p-6 bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-200 dark:border-amber-800 mb-8">
          <div className="flex items-start gap-4">
            <AlertTriangle size={24} className="text-amber-700 dark:text-amber-400 flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-lg font-bold text-amber-900 dark:text-amber-200 mb-2">
                Digital Products Notice
              </h2>
              <p className="text-sm text-amber-800 dark:text-amber-300">
                Due to the digital nature of our products, <strong>refunds are only available BEFORE you download the product</strong>.
                Once a product has been successfully downloaded, the refund option is automatically locked.
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="card p-8 space-y-8">
          {/* Overview */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Refund Eligibility Overview
            </h2>

            {/* Eligible */}
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg mb-4">
              <div className="flex items-start gap-3">
                <CheckCircle size={20} className="text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-green-900 dark:text-green-200 mb-1">
                    Eligible for Refund
                  </h3>
                  <p className="text-sm text-green-800 dark:text-green-300">
                    Purchased within 7 days AND product has NOT been downloaded
                  </p>
                </div>
              </div>
            </div>

            {/* Not Eligible */}
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-start gap-3">
                <XCircle size={20} className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-red-900 dark:text-red-200 mb-1">
                    NOT Eligible for Refund
                  </h3>
                  <p className="text-sm text-red-800 dark:text-red-300">
                    Product has been downloaded OR purchase is older than 7 days
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 1 */}
          <section className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
              <Download size={24} className="text-purple-600 dark:text-purple-400" />
              1. Download Lock Policy
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                To protect our digital products from abuse, we implement an automatic refund lock system:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li><strong>Before Download:</strong> Full refund available (within 7-day window)</li>
                <li><strong>After Download:</strong> Refunds are automatically locked and unavailable</li>
                <li><strong>Tracking:</strong> All download attempts are logged and tracked</li>
                <li><strong>No Exceptions:</strong> This policy applies to all digital products without exception</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                <strong>Important:</strong> Please review product descriptions carefully before downloading.
              </p>
            </div>
          </section>

          {/* Section 2 */}
          <section className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              2. 7-Day Refund Window
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                All refund requests must be submitted within <strong>7 days of purchase</strong>:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li>Day 0-7 after purchase: Refund available (if not downloaded)</li>
                <li>After 7 days: No refunds available, regardless of download status</li>
                <li>The 7-day period starts from the moment of successful payment</li>
              </ul>
            </div>
          </section>

          {/* Section 3 */}
          <section className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              3. How to Request a Refund
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                If you are eligible for a refund (within 7 days and product not downloaded):
              </p>
              <ol className="list-decimal list-inside space-y-3 text-gray-700 dark:text-gray-300 mb-4">
                <li className="pl-2">
                  <strong>Contact Support:</strong> Email us at{' '}
                  <a href="mailto:refunds@foxie.systems" className="text-primary-600 dark:text-primary-400 hover:underline">
                    refunds@foxie.systems
                  </a>
                </li>
                <li className="pl-2">
                  <strong>Provide Details:</strong> Include your order ID and email address
                </li>
                <li className="pl-2">
                  <strong>Verification:</strong> We will verify that the product has not been downloaded
                </li>
                <li className="pl-2">
                  <strong>Processing:</strong> Approved refunds are processed within 5-10 business days
                </li>
                <li className="pl-2">
                  <strong>Refund Method:</strong> Refunds are issued to your original payment method
                </li>
              </ol>
            </div>
          </section>

          {/* Section 4 */}
          <section className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
              <Lock size={24} className="text-red-600 dark:text-red-400" />
              4. Non-Refundable Situations
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Refunds are <strong>NOT available</strong> in the following situations:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li><strong>Downloaded Products:</strong> Any product that has been successfully downloaded</li>
                <li><strong>Expired Window:</strong> Purchases older than 7 days</li>
                <li><strong>Change of Mind:</strong> After accessing/viewing the product content</li>
                <li><strong>Technical Issues:</strong> Problems with your own device or internet connection</li>
                <li><strong>User Error:</strong> Purchasing the wrong product (check descriptions carefully)</li>
                <li><strong>Performance Claims:</strong> Dissatisfaction with strategy performance or outcomes</li>
                <li><strong>Gambling Losses:</strong> Any losses incurred while using the strategies</li>
              </ul>
            </div>
          </section>

          {/* Section 5 */}
          <section className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              5. Processing Time
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Once a refund is approved:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li><strong>Credit/Debit Card:</strong> 5-10 business days to appear on your statement</li>
                <li><strong>PayPal:</strong> 3-5 business days</li>
                <li><strong>Verification:</strong> We review all refund requests within 1-2 business days</li>
                <li><strong>Notification:</strong> You'll receive an email confirmation when the refund is processed</li>
              </ul>
            </div>
          </section>

          {/* Section 6 */}
          <section className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              6. Exceptional Circumstances
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                We may consider refunds outside our standard policy in exceptional cases:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li><strong>Platform Errors:</strong> If we made an error in product delivery or description</li>
                <li><strong>Duplicate Charges:</strong> Accidental double purchases</li>
                <li><strong>Technical Failures:</strong> If our system failed to deliver the purchased product</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                These cases are reviewed individually on a case-by-case basis.
              </p>
            </div>
          </section>

          {/* Section 7 */}
          <section className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              7. Chargebacks
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                <strong>Please contact us before initiating a chargeback.</strong>
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                If you initiate a chargeback without contacting us:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li>We will provide evidence of product delivery to your payment provider</li>
                <li>Your account may be suspended pending resolution</li>
                <li>Download logs and transaction records will be submitted as evidence</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Most issues can be resolved quickly through our support team.
              </p>
            </div>
          </section>

          {/* Section 8 */}
          <section className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              8. Contact Us
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                For refund requests or questions:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li>
                  Refunds:{' '}
                  <a href="mailto:refunds@foxie.systems" className="text-primary-600 dark:text-primary-400 hover:underline">
                    refunds@foxie.systems
                  </a>
                </li>
                <li>
                  General Support:{' '}
                  <a href="mailto:support@foxie.systems" className="text-primary-600 dark:text-primary-400 hover:underline">
                    support@foxie.systems
                  </a>
                </li>
                <li>
                  Contact Form: <Link to="/contact" className="text-primary-600 dark:text-primary-400 hover:underline">Visit our contact page</Link>
                </li>
              </ul>
            </div>
          </section>
        </div>

        {/* Footer Links */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center text-sm">
          <Link to="/terms" className="text-primary-600 dark:text-primary-400 hover:underline">
            Terms of Service
          </Link>
          <span className="text-gray-400">•</span>
          <Link to="/privacy-policy" className="text-primary-600 dark:text-primary-400 hover:underline">
            Privacy Policy
          </Link>
          <span className="text-gray-400">•</span>
          <Link to="/contact" className="text-primary-600 dark:text-primary-400 hover:underline">
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
}
